import {
  PageLayout,
  Section,
} from "@/components/layout";
import { Card, CardContent } from "@/components/ui/card";
import { useClientTranslation } from "@/hooks/use-client-translation";
import { Euro, Split, Lightbulb } from "lucide-react";
import { testimonials } from "@/data/testimonials";
import type { Testimonial } from "@/data/testimonials";

// Image paths from public folder
const lessFrictionImage = "/images/less_friction_placeholder.png";
const moreAvenueImage = "/images/more_avenue_placeholder.png";
const betterGuestsImage = "/images/better_guests_placeholder.png";

/* -------------------------------------------------------------------------- */
/*                              Hero Section                                  */
/* -------------------------------------------------------------------------- */

function HeroSection() {
  const { t } = useClientTranslation();

  return (
    <Section className="relative py-12 md:py-20">
      <div className="mx-auto max-w-[1280px] flex flex-col gap-10 md:gap-16">
        {/* Hero heading */}
        <div className="flex flex-col items-center justify-center gap-3">
          <span className="tagline">
            {t("forRestaurants.hero.label")}
          </span>
          <h1 className="text-[36px] md:text-4xl lg:text-5xl font-medium leading-[120%] tracking-[-0.01em] text-center">
            {t("forRestaurants.hero.title")}
          </h1>
        </div>

        {/* Feature Cards */}
        <div className="flex flex-col md:flex-row gap-8">
          {/* Large Revenue Card - takes 50% */}
          <Card className="group relative bg-white border border-[rgba(0,0,0,0.15)] rounded-lg flex flex-col w-full md:w-[640px] min-h-0 md:min-h-[322px]">
            <CardContent className="p-6 md:p-12 flex flex-col justify-center">
              <div className="flex flex-col gap-5 md:gap-6">
                <Euro className="h-12 w-12 shrink-0" />
                <div className="flex flex-col gap-5 md:gap-6">
                  <h3 className="text-[32px] md:text-[44px] font-medium leading-[120%] tracking-[-0.01em]">
                    {t("forRestaurants.features.revenue.title")}
                  </h3>
                  <p className="text-base font-normal leading-[150%]">
                    {t("forRestaurants.features.revenue.description")}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tipping Card - takes 25% */}
          <Card className="group relative bg-white border border-[rgba(0,0,0,0.15)] rounded-lg flex flex-col w-full md:flex-1 min-h-0 md:min-h-[322px]">
            <CardContent className="p-6 flex flex-col">
              <div className="flex flex-col gap-5">
                <Split className="h-12 w-12 shrink-0" />
                <div className="flex flex-col gap-2">
                  <h3 className="text-xl md:text-[28px] font-medium leading-[140%] tracking-[-0.01em]">
                    {t("forRestaurants.features.tipping.title")}
                  </h3>
                  <p className="text-base font-normal leading-[150%]">
                    {t("forRestaurants.features.tipping.description")}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Guests Card - takes 25% */}
          <Card className="group relative bg-white border border-[rgba(0,0,0,0.15)] rounded-lg flex flex-col w-full md:flex-1 min-h-0 md:min-h-[322px]">
            <CardContent className="p-6 flex flex-col">
              <div className="flex flex-col gap-5">
                <Lightbulb className="h-12 w-12 shrink-0" />
                <div className="flex flex-col gap-2">
                  <h3 className="text-xl md:text-[28px] font-medium leading-[140%] tracking-[-0.01em]">
                    {t("forRestaurants.features.guests.title")}
                  </h3>
                  <p className="text-base font-normal leading-[150%]">
                    {t("forRestaurants.features.guests.description")}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </Section>
  );
}

/* -------------------------------------------------------------------------- */
/*                            Growth Section                                  */
/* -------------------------------------------------------------------------- */

function GrowthSection() {
  const { t } = useClientTranslation();

  return (
    <Section className="border-t border-[rgba(0,0,0,0.15)] py-0">
      <div className="mx-auto max-w-[1280px] flex flex-col pb-12 md:pb-20 gap-10 md:gap-16">
        {/* Section Info - Numbered label */}
        <div className="flex items-center gap-5 h-16">
          <span className="text-base font-semibold leading-[150%] text-foreground">
            {t("forRestaurants.growth.label")}
          </span>
          <span className="text-base font-semibold leading-[150%] text-foreground">
            {t("forRestaurants.growth.subtitle")}
          </span>
        </div>

        {/* Content - Text and Image */}
        <div className="flex flex-col md:flex-row items-start gap-6 md:gap-16">
          {/* Text Content */}
          <div className="flex-1 flex flex-col max-w-[600px]">
            {/* Section Title */}
            <div className="flex flex-col gap-4">
              {/* Tagline */}
              <span className="tagline">
                {t("forRestaurants.growth.tagline")}
              </span>

              {/* Content */}
              <div className="flex flex-col gap-6">
                {/* Heading */}
                <h2 className="heading-2">
                  {t("forRestaurants.growth.title")}
                </h2>

                {/* Description */}
                <p className="text-lg leading-[150%] text-foreground">
                  {t("forRestaurants.growth.description")}
                </p>
              </div>
            </div>
          </div>

          {/* Image */}
          <div className="flex-1 w-full max-w-[600px]">
            <img
              src={lessFrictionImage}
              alt="Less friction"
              className="w-full h-[335px] md:h-[600px] object-cover rounded-lg"
            />
          </div>
        </div>
      </div>
    </Section>
  );
}

/* -------------------------------------------------------------------------- */
/*                          Efficiency Section                                */
/* -------------------------------------------------------------------------- */

function EfficiencySection() {
  const { t } = useClientTranslation();

  return (
    <Section className="border-t border-[rgba(0,0,0,0.15)] py-0">
      <div className="mx-auto max-w-[1280px] flex flex-col pb-12 md:pb-20 gap-10 md:gap-16">
        {/* Section Info - Numbered label */}
        <div className="flex items-center gap-5 h-16">
          <span className="text-base font-semibold leading-[150%] text-foreground">
            {t("forRestaurants.efficiency.label")}
          </span>
          <span className="text-base font-semibold leading-[150%] text-foreground">
            {t("forRestaurants.efficiency.subtitle")}
          </span>
        </div>

        {/* Content - Text and Image */}
        <div className="flex flex-col md:flex-row items-start gap-6 md:gap-16">
          {/* Text Content */}
          <div className="flex-1 flex flex-col max-w-[600px]">
            {/* Section Title */}
            <div className="flex flex-col gap-4">
              {/* Tagline */}
              <span className="tagline">
                {t("forRestaurants.efficiency.tagline")}
              </span>

              {/* Content */}
              <div className="flex flex-col gap-6">
                {/* Heading */}
                <h2 className="heading-2">
                  {t("forRestaurants.efficiency.title")}
                </h2>

                {/* Description */}
                <p className="text-lg leading-[150%] text-foreground">
                  {t("forRestaurants.efficiency.description")}
                </p>
              </div>
            </div>
          </div>

          {/* Image */}
          <div className="flex-1 w-full max-w-[600px]">
            <img
              src={moreAvenueImage}
              alt="Staff efficiency"
              className="w-full h-[335px] md:h-[600px] object-cover rounded-lg"
            />
          </div>
        </div>
      </div>
    </Section>
  );
}

/* -------------------------------------------------------------------------- */
/*                            Loyalty Section                                 */
/* -------------------------------------------------------------------------- */

function LoyaltySection() {
  const { t } = useClientTranslation();

  return (
    <Section className="border-t border-[rgba(0,0,0,0.15)] py-0">
      <div className="mx-auto max-w-[1280px] flex flex-col pb-12 md:pb-20 gap-10 md:gap-16">
        {/* Section Info - Numbered label */}
        <div className="flex items-center gap-5 h-16">
          <span className="text-base font-semibold leading-[150%] text-foreground">
            {t("forRestaurants.loyalty.label")}
          </span>
          <span className="text-base font-semibold leading-[150%] text-foreground">
            {t("forRestaurants.loyalty.subtitle")}
          </span>
        </div>

        {/* Content - Text and Image */}
        <div className="flex flex-col md:flex-row items-start gap-6 md:gap-16">
          {/* Text Content */}
          <div className="flex-1 flex flex-col max-w-[600px]">
            {/* Section Title */}
            <div className="flex flex-col gap-4">
              {/* Tagline */}
              <span className="tagline">
                {t("forRestaurants.loyalty.tagline")}
              </span>

              {/* Content */}
              <div className="flex flex-col gap-6">
                {/* Heading */}
                <h2 className="heading-2">
                  {t("forRestaurants.loyalty.title")}
                </h2>

                {/* Description */}
                <p className="text-lg leading-[150%] text-foreground">
                  {t("forRestaurants.loyalty.description")}
                </p>
              </div>
            </div>
          </div>

          {/* Image */}
          <div className="flex-1 w-full max-w-[600px]">
            <img
              src={betterGuestsImage}
              alt="Better guests"
              className="w-full h-[335px] md:h-[600px] object-cover rounded-lg"
            />
          </div>
        </div>
      </div>
    </Section>
  );
}

/* -------------------------------------------------------------------------- */
/*                         Testimonial Card                                   */
/* -------------------------------------------------------------------------- */

function TestimonialCard({ testimonial }: { testimonial: Testimonial }) {
  const { t } = useClientTranslation();
  const name = t(`testimonials.items.${testimonial.i18nKey}.name`);
  const quote = t(`testimonials.items.${testimonial.i18nKey}.quote`);

  return (
    <div className="flex flex-col items-center flex-1 min-w-0 w-full">
      {/* Photo - overlaps the card top */}
      <div className="relative z-10 mb-[-60px]">
        <div className="h-[120px] w-[120px] rounded-full overflow-hidden border-4 border-white shadow-lg bg-gray-100">
          <img
            src={testimonial.image}
            alt={name}
            className="h-full w-full object-cover"
            style={testimonial.imagePosition ? { objectPosition: testimonial.imagePosition } : undefined}
          />
        </div>
      </div>

      {/* Card */}
      <div className="flex flex-col items-center text-center gap-4 pt-[72px] pb-8 px-6 border border-[rgba(0,0,0,0.15)] rounded-lg w-full h-full bg-gradient-to-r from-purple-primary/5 to-white">
        <div className="flex flex-col items-center gap-1">
          <h3 className="text-xl md:text-2xl font-normal leading-[1.4] text-black">
            {name}
          </h3>
          <p className="text-base md:text-lg font-bold leading-[1.4] text-black">
            {testimonial.role} @ {testimonial.company}
          </p>
        </div>
        <p className="text-sm md:text-base font-normal leading-[1.6] text-black/80">
          {quote}
        </p>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                        Testimonials Section                                */
/* -------------------------------------------------------------------------- */

function TestimonialsSection() {
  const { t } = useClientTranslation();
  return (
    <Section className="border-t border-[rgba(0,0,0,0.15)] py-0">
      <div className="mx-auto max-w-[1280px] flex flex-col pb-12 md:pb-20 gap-10 md:gap-14">
        {/* Heading */}
        <div className="flex flex-col items-center justify-center gap-3">
          {t("testimonials.tagline") && <span className="tagline">{t("testimonials.tagline")}</span>}
          <h2 className="heading-2">{t("testimonials.title")}</h2>
        </div>

        {/* Cards */}
        <div className="flex flex-col md:flex-row items-stretch gap-12 md:gap-8">
          {testimonials.map((testimonial) => (
            <TestimonialCard
              key={testimonial.i18nKey}
              testimonial={testimonial}
            />
          ))}
        </div>
      </div>
    </Section>
  );
}

/* -------------------------------------------------------------------------- */
/*                        For Restaurants Page                                */
/* -------------------------------------------------------------------------- */

export default function ForRestaurantsPage() {
  return (
    <PageLayout activePage="for-restaurants">
      <main className="flex-1">
        <HeroSection />
        <GrowthSection />
        <EfficiencySection />
        <LoyaltySection />
        <TestimonialsSection />
      </main>
    </PageLayout>
  );
}


